// src/components/ui/ConnectWalletButton.tsx
"use client";
import { motion } from "framer-motion";
import { useAccount, useDisconnect, useSwitchChain, useChainId } from "wagmi";
import { useState, useEffect } from "react";
import { useAppKit } from "@reown/appkit/react";
import { soneium } from "../../config/wagmi";
import { CyberCorners } from "./CyberCorners";

const StatusDot = ({
  connected,
  wrongNetwork,
}: {
  connected: boolean;
  wrongNetwork: boolean;
}) => {
  if (!connected) return null;

  const color = wrongNetwork ? "#ef4444" : "#22c55e";
  const shadow = wrongNetwork
    ? "rgba(239, 68, 68, 0.8)"
    : "rgba(34, 197, 94, 0.8)";

  return (
    <div
      className="w-2 h-2 rounded-full animate-pulse"
      style={{
        backgroundColor: color,
        boxShadow: `0 0 8px ${shadow}`,
      }}
    />
  );
};

const Spinner = () => (
  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
);

interface ConnectWalletButtonProps {
  className?: string;
}

export default function ConnectWalletButton({
  className,
}: ConnectWalletButtonProps) {
  const { isConnected, isConnecting, address } = useAccount();
  const { mutate } = useDisconnect();
  const { mutateAsync } = useSwitchChain();
  const chainId = useChainId();
  const { open } = useAppKit();

  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isConnected && chainId !== soneium.id) {
      mutateAsync({ chainId: soneium.id });
    }
  }, [isConnected, chainId, mutateAsync]);

  const isWrongNetwork = isConnected && chainId !== soneium.id;

  const getButtonContent = () => {
    if (isConnecting) {
      return (
        <span className="flex items-center gap-2">
          <Spinner />
          <span>CONNECTING...</span>
        </span>
      );
    }

    if (!isConnected) {
      return "CONNECT WALLET";
    }

    if (isWrongNetwork) {
      return (
        <span className="flex items-center gap-2">
          <StatusDot connected={true} wrongNetwork={true} />
          <span>SWITCH NETWORK</span>
        </span>
      );
    }

    const shortened = `${address?.slice(0, 6)}...${address?.slice(-4)}`;

    if (isHovered) {
      return (
        <span className="flex items-center gap-2">
          <StatusDot connected={true} wrongNetwork={false} />
          <span>DISCONNECT</span>
        </span>
      );
    }

    return (
      <span className="flex items-center gap-2">
        <StatusDot connected={true} wrongNetwork={false} />
        <span>{shortened}</span>
      </span>
    );
  };

  const handleClick = () => {
    if (!isConnected) {
      open();
      return;
    }

    if (isWrongNetwork) {
      mutateAsync({ chainId: soneium.id });
      return;
    }

    mutate();
  };

  const borderColor = isWrongNetwork
    ? "rgba(239, 68, 68, 0.8)"
    : isConnected
    ? "rgba(34, 197, 94, 0.6)"
    : "rgba(255, 255, 255, 0.6)";

  const cornerColor = isWrongNetwork
    ? "#ef4444"
    : isConnected
    ? "#22c55e"
    : "#FFFFFF";

  const background = isWrongNetwork
    ? "linear-gradient(93.17deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%)"
    : isConnected
    ? "linear-gradient(93.17deg, rgba(34, 197, 94, 0.15) 0%, rgba(0, 212, 255, 0.15) 100%)"
    : "linear-gradient(93.17deg, rgba(255, 0, 128, 0.2) 0%, rgba(0, 212, 255, 0.2) 100%)";

  return (
    <motion.button
      whileTap={{ scale: 0.97 }}
      whileHover={{ scale: 1.02 }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={isConnecting}
      className={`cyber-border-cut relative overflow-hidden flex items-center justify-center gap-2 px-4 py-2 md:px-6 md:py-2.5 font-bold uppercase tracking-wide transition-all duration-300 cursor-pointer ${
        isConnecting ? "opacity-60 cursor-not-allowed" : ""
      } ${className ?? ""}`}
      style={
        {
          background,
          color: "#FFFFFF",
          borderColor,
          borderRadius: 0,
          "--border-color": borderColor,
        } as React.CSSProperties
      }
    >
      <CyberCorners color={cornerColor} />

      <span className="z-10 flex items-center gap-2 text-xs md:text-sm font-mono">
        {getButtonContent()}
      </span>
    </motion.button>
  );
}
